import * as O from 'fp-ts/Option'

import { IMaybe, maybe } from 'typescript-monads'
import { combineLatest, EMPTY, merge, Observable, of } from 'rxjs'
import { Inject, Injectable } from '@angular/core'
import { LiveEventService } from './event.service'
import {
  IRealtimeCameraInfoDictionary,
  IRealtimeEventResponseWithEventId
} from '../models/realtime-event-response.model'
import { catchError, filter, mergeMap, map, shareReplay, switchMap, take, tap } from 'rxjs/operators'
import { ActivatedRouteSnapshot, CanActivate, Params, Router, RouterStateSnapshot } from '@angular/router'
import { routeToUpgradePage, routeToSignupPage } from './routing.utility'
import { UserAgentService } from 'src/app/singleton-services/user-agent.service'
import { VerticalService } from 'src/app/singleton-services/vertical.service'
import { flow, pipe } from 'fp-ts/function'
import { fromMaybe } from '../../../fp/from-maybe'
import { LOGGER_SERVICE } from '../../logger/logger.config'
import { LoggerService } from '../../logger/logger.interface'
import { HttpErrorResponse } from '@angular/common/http'
import { AuthService } from '../../singleton-services/auth/auth.service'
import { ResourceType } from '../../singleton-services/auth/auth.interfaces'
import { SubscriptionService } from '../../shared/services/subscription.service'
import { getIdFromSlug } from 'src/app/shared/utility-functions/slug.utility'
import { CookieService } from 'src/app/singleton-services/cookie.service'
import { mobileSmartBannerCookie } from 'src/app/singleton-services/mobile-smart-banner.service'

interface CanViewWrapper {
  readonly liveEvent: IMaybe<IRealtimeEventResponseWithEventId>
  readonly canView: boolean
  readonly shouldRouteToInterstitial?: boolean
}

const getLiveIdFromParam = (param: string) => (isNaN(+param) ? getIdFromSlug(param) : +param)

const handlerLiveEventServiceError = (router: Router, log: LoggerService) => (_err: any) => {
  log.error('Unable to get live event from live api.', { _err })
  router.navigate(['error'])
  return EMPTY
}

const routeToAppInterstitial = (router: Router) => (liveEventSlug: string, queryParams: Params) => {
  router.navigate(['app-interstitial'], { queryParams: { ...queryParams, redirect: `/live/${liveEventSlug}` } })
}

const isEventPremium = (evt: IRealtimeEventResponseWithEventId) => evt.premium !== 'FREE'

const userCanViewEvent =
  (userEntitled: boolean) =>
  (liveEvent: IMaybe<IRealtimeEventResponseWithEventId>): CanViewWrapper => {
    return liveEvent
      .map(evt => {
        const isPremium = isEventPremium(evt)
        return {
          canView: (evt.disableLogin && !isPremium) || userEntitled || !isPremium || false,
          liveEvent: maybe(evt)
        }
      })
      .valueOr({
        canView: false,
        liveEvent: maybe<IRealtimeEventResponseWithEventId>()
      })
  }

// prettier-ignore
const tryInjectEventIntoRouteData = (state: RouterStateSnapshot) => (
  realtimeEventInfo: unknown
): void => {
  pipe(
    O.fromNullable(state.root?.firstChild?.firstChild),
    O.map(snapshot => {
      snapshot.data = {
      ...snapshot.data,
      realtimeEventInfo
    }})
  )
}

@Injectable()
export class LiveEventGuard implements CanActivate {
  constructor(
    private router: Router,
    private les: LiveEventService,
    private readonly userAgentService: UserAgentService,
    private readonly verticalService: VerticalService,
    private readonly authService: AuthService,
    private readonly subscriptionService: SubscriptionService,
    private readonly cookieService: CookieService,
    @Inject(LOGGER_SERVICE) private readonly log: LoggerService
  ) {}

  // The user can view the live event:
  // if the event login is disabled and the event is free
  // if the user is logged in and the user is premium
  // if the user is logged in and the event is free
  // UNLESS on mobile device - user is shown the app interstitial and prompted to download the app
  canActivate(ars: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const param = ars.params.id
    const liveId = getLiveIdFromParam(param)
    const queryParams = ars.queryParams

    const attemptFetchFromLiveApi = (
      id: string,
      _err: HttpErrorResponse
    ): Observable<IRealtimeEventResponseWithEventId> => {
      return this.les.getLiveEventWithStreams(id).pipe(
        map(liveEvent => {
          const liveApiEventData = liveEvent.data
          return {
            ...liveApiEventData,
            siteId: 0,
            title: liveApiEventData.name,
            schedule: '',
            description: liveApiEventData.name,
            liveEventUrl: '',
            disableLogin: false,
            shortName: liveApiEventData.name,
            isDVR: liveApiEventData.dvrEnabled,
            eventStatus: liveApiEventData.status,
            liveEventId: liveApiEventData.id,
            streams: liveApiEventData.streams.reduce(
              (acc, curr) => ({ ...acc, [curr.id]: curr }),
              [] as unknown as IRealtimeCameraInfoDictionary
            )
          }
        })
      )
    }

    const liveEventRealtimeStream$ = this.les.getLiveEventRealtimeStream(liveId).pipe(
      take(1),
      catchError((err: HttpErrorResponse) => {
        this.log.error('Unable to get live event from firebase.', { err })
        return attemptFetchFromLiveApi(liveId.toString(), err).pipe(map(event => maybe(event)))
      }),
      catchError(handlerLiveEventServiceError(this.router, this.log)),
      tap(flow(fromMaybe, O.map(tryInjectEventIntoRouteData(state))))
    )

    const hasAccess$ = liveEventRealtimeStream$.pipe(
      map(maybeLiveEvent => maybeLiveEvent.valueOrUndefined()),
      switchMap(liveEvent => {
        if (!liveEvent) return of(false)
        return this.authService.hasAccess({ id: liveId, premium: isEventPremium(liveEvent) }, ResourceType.LiveEvent)
      }),
      take(1)
    )

    const liveEventName$ = liveEventRealtimeStream$.pipe(
      map(maybeLiveEvent => maybeLiveEvent.valueOrUndefined()),
      map(liveEvent => liveEvent?.name),
      take(1)
    )

    const baseCanViewLiveEvent$ = combineLatest([liveEventRealtimeStream$, hasAccess$]).pipe(
      map(([liveEventStream, isEntitled]) => userCanViewEvent(isEntitled)(liveEventStream)),
      shareReplay(1)
    )

    const canViewAppInterstitial$: Observable<CanViewWrapper | undefined> = combineLatest([
      baseCanViewLiveEvent$,
      this.verticalService.isMileSplitOrVarsity$
    ]).pipe(
      map(([baseCanView, isMileplitOrVarsity]) => {
        const wantsToWatchOnWeb = queryParams.watchOnWeb
        const hasDismissedBanner = this.cookieService.get(mobileSmartBannerCookie) === true
        const shouldRouteToInterstitial =
          this.userAgentService.deviceIsAndroidOrSupportedIos &&
          !wantsToWatchOnWeb &&
          !isMileplitOrVarsity &&
          !hasDismissedBanner

        if (shouldRouteToInterstitial) {
          return {
            canView: false,
            liveEvent: baseCanView.liveEvent,
            shouldRouteToInterstitial: true
          }
        }

        return undefined
      })
    )

    const canViewLiveEvent$ = combineLatest([baseCanViewLiveEvent$, canViewAppInterstitial$]).pipe(
      map(([baseCanView, canViewAppInterstitial]) => {
        if (!!canViewAppInterstitial && baseCanView.canView) {
          return canViewAppInterstitial
        }

        return baseCanView
      })
    )

    const canViewVideoPlayer$ = canViewLiveEvent$.pipe(
      filter(a => a.canView),
      map(() => true)
    )

    const userCannotViewAnyVideoPlayer = canViewLiveEvent$.pipe(
      filter(a => !a.canView),
      shareReplay(1)
    )
    const navigateToNotFound = () => this.router.navigate(['**'], { skipLocationChange: true })
    const navigateToEventHub = (slug: string) => {
      this.router.navigate([slug])
    }

    const liveEventNotFound$ = userCannotViewAnyVideoPlayer.pipe(
      filter(a => a.liveEvent.match({ some: _ => false, none: () => true })),
      mergeMap(() =>
        this.les.getLiveEventConsolidated$(liveId).pipe(
          tap(eventSummary => {
            maybe(eventSummary.event)
              .flatMapAuto(evt => evt.slug_uri)
              .tap({
                some: navigateToEventHub,
                none: navigateToNotFound
              })
          }),
          map(_ => false),
          catchError(_ => {
            return of(false).pipe(tap(_a => navigateToNotFound()))
          })
        )
      )
    )

    const userSubscriptions$ = this.authService.userIdentity$.pipe(
      switchMap(user => {
        if (!user) return of(null)
        return this.subscriptionService.getAllSubscriptions(user.id, true)
      })
    )

    const userCannotViewPlayerForOtherReasons = combineLatest([
      userCannotViewAnyVideoPlayer,
      userSubscriptions$,
      liveEventName$
    ]).pipe(
      filter(([userCannotView]) => userCannotView.liveEvent.match({ some: _ => true, none: () => false })),
      tap(([userCannotView, userSubs, liveEventName]) => {
        if (userCannotView.shouldRouteToInterstitial) {
          routeToAppInterstitial(this.router)(param, queryParams)
        } else if (!userCannotView.canView && userSubs && userSubs.length > 0) {
          routeToUpgradePage(this.router)(param, queryParams, liveEventName)
        } else {
          routeToSignupPage(this.router)(param, queryParams)
        }
      }),
      map(([userCannotView]) => userCannotView.canView)
    )

    return merge(canViewVideoPlayer$, liveEventNotFound$, userCannotViewPlayerForOtherReasons).pipe(take(1))
  }
}
